import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, timeout } from 'rxjs';

export enum TranslationMode {
  ENABLED,
  DISABLED,
}

interface SpeechResponse {
  text: string;
  translated_text: string | null;
  translated_audio: string | null;
}

interface ChunkRecorder {
  stop(): void;
}

/** A translated phrase: isLocal = spoken by this user (and sent to the remote side), otherwise heard from the remote side. */
export interface TranslationResult {
  isLocal: boolean;
  text: string;
  translation: string | null;
  sourceLanguage: string;
  targetLanguage: string;
}

/** A phrase back from the server, ready to be subtitled and spoken. */
interface Phrase {
  result: TranslationResult;
  audio: string | null;
}

/** One recording between two pauses. */
interface Segment {
  recorder: MediaRecorder;
  parts: Blob[];
  startedAt: number;
  speechMs: number;
  lastSpeechAt: number;
}

/** Per direction: phrases are processed in parallel but subtitled and spoken in the order they were said. */
interface VoiceChannel {
  nextSeq: number;
  nextToDeliver: number;
  pending: Map<number, Phrase | null>;
  queue: string[];
  playing: boolean;
  skipTimer?: ReturnType<typeof setTimeout>;
}

// Voice activity detection: a phrase is sent as soon as the speaker pauses
const VAD_INTERVAL_MS = 50;
const SILENCE_MS = 600;
const MIN_SPEECH_MS = 250;
const MAX_SEGMENT_MS = 9_000;
const IDLE_SEGMENT_MS = 3_000;
const MIN_SPEECH_RMS = 0.01;
const REQUEST_TIMEOUT_MS = 15_000;
// After translation is switched off, a translated voice still playing keeps the original voice lowered
const DUCKED_GAIN = 0.3;
const PLAY_START_TIMEOUT_MS = 4_000;

/** Cuts WebRTC audio into phrases, sends them to the Google Speech/Translate server flow and plays back the translated voice. */
@Injectable({ providedIn: 'root' })
export class AudioTranslatorService {
  private readonly isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
  private speechApiUrl = '';
  private localRecorder?: ChunkRecorder;
  private remoteRecorder?: ChunkRecorder;
  private sourceLanguage = 'it-IT';
  private targetLanguage = 'en-GB';
  private tenantId = 'undefined';
  private connectionId = 'undefined';
  private backendUrl = '';
  private speechLogEnabled = true;

  // Same audio graph used with Azure: the microphone and the remote audio pass through an AudioContext,
  // so the translated voice can be mixed in (local: sent to the remote side, remote: heard by this user)
  private audioContext?: AudioContext;
  private localMediaStream: MediaStream | null = null;
  private localGain?: GainNode;
  private destinationNode?: MediaStreamAudioDestinationNode;
  private remoteAudioContext?: AudioContext;
  private remoteMediaStream: MediaStream | null = null;
  private remoteGain?: GainNode;
  private remoteDestinationNode?: MediaStreamAudioDestinationNode;
  // Muted element that keeps the remote WebRTC audio flowing into the AudioContext (see addRemoteStream)
  private remoteAudioElement?: HTMLAudioElement;
  private remoteSourceNode?: MediaStreamAudioSourceNode;
  // Bumped at the end of a call: phrases of the previous call still in flight are dropped
  private generation = 0;
  private readonly channels = { local: this.createChannel(), remote: this.createChannel() };
  // Directions being translated: their original voice does not reach the listener, only the translation does
  private readonly translating = { local: false, remote: false };

  readonly translations$ = new Subject<TranslationResult>();

  constructor(private readonly http: HttpClient) {}

  setTenantAndConnection(tenantId: string, connectionId: string): void {
    this.tenantId = tenantId;
    this.connectionId = connectionId;
  }

  setBackendUrl(url: string): void {
    this.backendUrl = url.replace(/\/$/, '');
  }

  setSpeechLogEnabled(enabled: boolean): void {
    this.speechLogEnabled = enabled;
  }

  setSpeechApiUrl(url: string): void {
    this.speechApiUrl = url.replace(/\/$/, '');
  }

  setupLocalAudioGraph(localMediaStream: MediaStream): Promise<MediaStream> {
    return new Promise<MediaStream>((resolve, reject) => {
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }
      this.localMediaStream = localMediaStream;
      setTimeout(() => {
        if (!this.audioContext || this.localMediaStream === null) {
          reject('No media stream');
          return;
        }
        const audioContext = this.audioContext as any;
        const sourceNode: MediaStreamAudioSourceNode = this.isFirefox && audioContext.createMediaStreamTrackSource
          ? audioContext.createMediaStreamTrackSource(this.localMediaStream)
          : this.audioContext.createMediaStreamSource(this.localMediaStream);
        this.localGain = this.audioContext.createGain();
        this.destinationNode = this.audioContext.createMediaStreamDestination();
        sourceNode.connect(this.localGain).connect(this.destinationNode);
        // A suspended context sends silence to the remote side
        void this.audioContext.resume();
        console.info('[traduzione] audio locale pronto, AudioContext:', this.audioContext.state);
        this.watchOutgoingLevel(this.audioContext, this.destinationNode);
        resolve(this.destinationNode.stream);
      }, 200);
    });
  }

  addRemoteStream(stream: MediaStream | MediaStreamTrack): Promise<MediaStream> {
    this.remoteMediaStream = stream instanceof MediaStreamTrack ? new MediaStream([stream]) : stream;
    if (!this.remoteAudioContext) {
      this.remoteAudioContext = new AudioContext();
    }
    return new Promise<MediaStream>((resolve, reject) => {
      setTimeout(() => {
        if (!this.remoteAudioContext || this.remoteMediaStream === null) {
          reject('No media stream');
          return;
        }
        // After a reconnection the old remote audio is replaced, not kept playing next to the new one
        this.releaseRemoteAudio();
        const remoteAudioContext = this.remoteAudioContext;
        const audio = new Audio();
        this.remoteAudioElement = audio;
        audio.srcObject = this.remoteMediaStream;
        const remoteGain = remoteAudioContext.createGain();
        const remoteDestinationNode = remoteAudioContext.createMediaStreamDestination();
        this.remoteGain = remoteGain;
        this.remoteDestinationNode = remoteDestinationNode;
        audio.onloadedmetadata = () => {
          if (audio.srcObject === null) {
            return;
          }
          // Chrome feeds a remote WebRTC stream into the AudioContext only while a (muted) element plays it
          const sourceNode = remoteAudioContext.createMediaStreamSource(audio.srcObject as MediaStream);
          this.remoteSourceNode = sourceNode;
          audio.play();
          audio.muted = true;
          sourceNode.connect(remoteGain).connect(remoteDestinationNode);
          void remoteAudioContext.resume();
          console.info('[traduzione] audio remoto pronto, AudioContext:', remoteAudioContext.state);
          setTimeout(() => resolve(remoteDestinationNode.stream), 500);
        };
      }, 200);
    });
  }

  async onLocalTranslationModeChange(mode: TranslationMode, stream?: MediaStream): Promise<void> {
    if (mode === TranslationMode.DISABLED) {
      this.stopRecorder(this.localRecorder);
      this.localRecorder = undefined;
      this.translating.local = false;
      this.duck(true, this.channels.local.playing);
      return;
    }
    // Record the raw microphone, not the mixed stream, so the translated voice is not transcribed again
    const microphone = this.localMediaStream ?? stream;
    if (!microphone) {
      throw new Error('Flusso audio locale non disponibile');
    }
    await this.audioContext?.resume();
    this.stopRecorder(this.localRecorder);
    this.localRecorder = this.startRecorder(microphone, this.sourceLanguage, this.targetLanguage, true);
    this.translating.local = true;
    this.duck(true, false);
  }

  async onRemoteTranslationModeChange(mode: TranslationMode, stream?: MediaStream): Promise<void> {
    if (mode === TranslationMode.DISABLED) {
      this.stopRecorder(this.remoteRecorder);
      this.remoteRecorder = undefined;
      this.translating.remote = false;
      this.duck(false, this.channels.remote.playing);
      return;
    }
    const remoteAudio = this.remoteMediaStream ?? stream;
    if (!remoteAudio) {
      throw new Error('Flusso audio remoto non disponibile');
    }
    await this.remoteAudioContext?.resume();
    this.stopRecorder(this.remoteRecorder);
    this.remoteRecorder = this.startRecorder(remoteAudio, this.targetLanguage, this.sourceLanguage, false);
    this.translating.remote = true;
    this.duck(false, false);
  }

  /** End of the call: recorders stopped, microphone released, audio contexts closed, pending phrases dropped. */
  dispose(): void {
    this.stopRecorder(this.localRecorder);
    this.stopRecorder(this.remoteRecorder);
    this.localRecorder = undefined;
    this.remoteRecorder = undefined;
    this.generation++;
    this.translating.local = false;
    this.translating.remote = false;
    this.localMediaStream?.getTracks().forEach(track => track.stop());
    this.localMediaStream = null;
    this.releaseRemoteAudio();
    this.remoteMediaStream = null;
    void this.audioContext?.close();
    void this.remoteAudioContext?.close();
    this.audioContext = undefined;
    this.remoteAudioContext = undefined;
    this.localGain = undefined;
    this.destinationNode = undefined;
    this.remoteGain = undefined;
    this.remoteDestinationNode = undefined;
    for (const channel of [this.channels.local, this.channels.remote]) {
      clearTimeout(channel.skipTimer);
      Object.assign(channel, this.createChannel(), { skipTimer: undefined });
    }
  }

  private releaseRemoteAudio(): void {
    this.remoteSourceNode?.disconnect();
    this.remoteSourceNode = undefined;
    if (this.remoteAudioElement) {
      this.remoteAudioElement.pause();
      this.remoteAudioElement.srcObject = null;
      this.remoteAudioElement = undefined;
    }
  }

  onSourceLanguageChange(sourceLanguage: string): void {
    this.sourceLanguage = this.toLocale(sourceLanguage);
  }

  onTargetLanguageChange(targetLanguage: string): void {
    this.targetLanguage = this.toLocale(targetLanguage);
  }

  getLanguages(): { value: string; label: string }[] {
    return [
      { value: 'it-IT', label: 'Italiano' },
      { value: 'en-GB', label: 'English' },
      { value: 'en-US', label: 'American English' },
      { value: 'de-DE', label: 'Deutsch' },
      { value: 'es-ES', label: 'Español' },
      { value: 'fr-FR', label: 'Français' },
      { value: 'pt-BR', label: 'Português' },
      { value: 'ru-RU', label: 'Русский' },
      { value: 'ar-SA', label: 'العربية' },
      { value: 'zh-CN', label: '中文' },
      { value: 'hi-IN', label: 'हिन्दी' },
      { value: 'tr-TR', label: 'Türkçe' },
      { value: 'pl-PL', label: 'Polski' },
      { value: 'ro-RO', label: 'Română' },
      { value: 'uk-UA', label: 'Українська' },
      { value: 'nl-NL', label: 'Nederlands' },
    ];
  }

  private createChannel(): VoiceChannel {
    return { nextSeq: 0, nextToDeliver: 0, pending: new Map(), queue: [], playing: false };
  }

  private startRecorder(stream: MediaStream, sourceLanguage: string, targetLanguage: string, isLocal: boolean): ChunkRecorder {
    if (!this.speechApiUrl) {
      throw new Error('Endpoint speech-to-text non configurato');
    }
    if (typeof MediaRecorder === 'undefined') {
      throw new Error('Registrazione audio non supportata dal browser');
    }

    const audioStream = new MediaStream(stream.getAudioTracks());
    if (!audioStream.getAudioTracks().length) {
      throw new Error('Il flusso non contiene una traccia audio');
    }
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : undefined;

    // The volume of the raw audio decides where a phrase ends
    const context = (isLocal ? this.audioContext : this.remoteAudioContext) ?? new AudioContext();
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    const analyserSource = context.createMediaStreamSource(audioStream);
    analyserSource.connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    let noiseFloor = MIN_SPEECH_RMS;

    // A new MediaRecorder per phrase gives the server complete WebM files; the next one starts
    // before the previous one stops, so nothing is lost between two phrases
    const startSegment = (): Segment => {
      const recorder = new MediaRecorder(audioStream, mimeType ? { mimeType } : undefined);
      const segment: Segment = { recorder, parts: [], startedAt: Date.now(), speechMs: 0, lastSpeechAt: 0 };
      recorder.addEventListener('dataavailable', ({ data }) => {
        if (data.size > 0) {
          segment.parts.push(data);
        }
      });
      recorder.start();
      return segment;
    };
    const finishSegment = (segment: Segment, send: boolean) => {
      const seq = send ? this.channels[isLocal ? 'local' : 'remote'].nextSeq++ : -1;
      const generation = this.generation;
      segment.recorder.addEventListener('stop', () => {
        if (seq < 0) {
          return;
        }
        if (segment.parts.length) {
          const chunk = new Blob(segment.parts, { type: segment.recorder.mimeType });
          void this.transcribeChunk(chunk, sourceLanguage, targetLanguage, isLocal, seq, generation);
        } else if (generation === this.generation) {
          this.deliver(isLocal, seq, null);
        }
      });
      this.stopMediaRecorder(segment.recorder);
    };

    let segment = startSegment();
    const timer = setInterval(() => {
      if (audioStream.getAudioTracks().every(track => track.readyState === 'ended')) {
        stop();
        return;
      }
      analyser.getFloatTimeDomainData(samples);
      const rms = Math.sqrt(samples.reduce((sum, value) => sum + value * value, 0) / samples.length);
      // The background noise level drops immediately and rises slowly, so speech does not raise it
      noiseFloor = rms < noiseFloor ? rms : noiseFloor + (rms - noiseFloor) * 0.01;
      const now = Date.now();
      if (rms > Math.max(MIN_SPEECH_RMS, noiseFloor * 2.5)) {
        segment.speechMs += VAD_INTERVAL_MS;
        segment.lastSpeechAt = now;
      }
      const age = now - segment.startedAt;
      const hasSpeech = segment.speechMs >= MIN_SPEECH_MS;
      if (hasSpeech && (now - segment.lastSpeechAt >= SILENCE_MS || age >= MAX_SEGMENT_MS)) {
        const done = segment;
        segment = startSegment();
        finishSegment(done, true);
      } else if (!hasSpeech && age >= IDLE_SEGMENT_MS) {
        // Only silence or noise: thrown away, not sent to the server
        const done = segment;
        segment = startSegment();
        finishSegment(done, false);
      }
    }, VAD_INTERVAL_MS);

    let active = true;
    const stop = () => {
      if (!active) {
        return;
      }
      active = false;
      clearInterval(timer);
      analyserSource.disconnect();
      // The phrase in progress is still sent for translation
      finishSegment(segment, segment.speechMs >= MIN_SPEECH_MS);
    };
    return { stop };
  }

  private async transcribeChunk(chunk: Blob, language: string, targetLanguage: string, isLocal: boolean, seq: number, generation: number): Promise<void> {
    const deliver = (phrase: Phrase | null) => {
      if (generation === this.generation) {
        this.deliver(isLocal, seq, phrase);
      }
    };
    try {
      const content = await this.toBase64(chunk);
      this.http.post<SpeechResponse | { data: SpeechResponse }>(this.speechApiUrl, {
        content,
        filename: 'audio.webm',
        language,
        target_language: targetLanguage,
        with_audio: true,
      }).pipe(timeout(REQUEST_TIMEOUT_MS)).subscribe({
        next: body => {
          // Django wraps every response as {success, data}
          const response: SpeechResponse = 'data' in body ? body.data : body;
          if (response.text) {
            console.info(`[traduzione] frase #${seq} ${isLocal ? 'tua' : 'del tecnico'}: "${response.text}" -> "${response.translated_text}", voce: ${response.translated_audio ? 'sì' : 'no'}`);
          }
          deliver(response.text ? {
            result: { isLocal, text: response.text, translation: response.translated_text, sourceLanguage: language, targetLanguage },
            audio: response.translated_audio,
          } : null);
        },
        error: error => {
          console.error('Speech-to-text non riuscito', error);
          deliver(null);
        },
      });
    } catch (error) {
      console.error('Impossibile preparare il frammento audio', error);
      deliver(null);
    }
  }

  /** Responses can arrive out of order: each phrase waits for the ones said before it (a failed one is skipped). */
  private deliver(isLocal: boolean, seq: number, phrase: Phrase | null): void {
    const channel = this.channels[isLocal ? 'local' : 'remote'];
    channel.pending.set(seq, phrase);
    while (channel.pending.has(channel.nextToDeliver)) {
      const ready = channel.pending.get(channel.nextToDeliver);
      channel.pending.delete(channel.nextToDeliver);
      channel.nextToDeliver++;
      if (!ready) {
        continue;
      }
      console.info(`[traduzione] frase #${channel.nextToDeliver - 1} ${isLocal ? 'tua' : 'del tecnico'} consegnata (sottotitolo${ready.audio ? ' + voce' : ''})`);
      this.translations$.next(ready.result);
      if (ready.audio) {
        channel.queue.push(ready.audio);
        this.playNext(isLocal);
      }
      this.sendInfoToServer({
        type: 'translation', isLocal, transcript: ready.result.text, translation: ready.result.translation,
        sourceLanguage: ready.result.sourceLanguage, targetLanguage: ready.result.targetLanguage, timestamp: Date.now(),
      });
    }
    this.armSkip(isLocal);
  }

  /** A phrase that never comes back must not hold back the ones said after it. */
  private armSkip(isLocal: boolean): void {
    const channel = this.channels[isLocal ? 'local' : 'remote'];
    if (channel.skipTimer || !channel.pending.size) {
      return;
    }
    const waitingFor = channel.nextToDeliver;
    console.info(`[traduzione] ${channel.pending.size} frasi pronte aspettano la #${waitingFor}`);
    channel.skipTimer = setTimeout(() => {
      channel.skipTimer = undefined;
      if (channel.nextToDeliver === waitingFor && !channel.pending.has(waitingFor)) {
        console.warn(`[traduzione] frase #${waitingFor} mai tornata, la salto`);
        this.deliver(isLocal, waitingFor, null);
      } else {
        this.armSkip(isLocal);
      }
    }, REQUEST_TIMEOUT_MS + 5_000);
  }

  /** Local phrases go into the outgoing call audio, remote ones into the audio this user hears. */
  private playNext(isLocal: boolean): void {
    const channel = this.channels[isLocal ? 'local' : 'remote'];
    const context = isLocal ? this.audioContext : this.remoteAudioContext;
    const destination = isLocal ? this.destinationNode : this.remoteDestinationNode;
    if (channel.playing || !channel.queue.length) {
      return;
    }
    if (!context || !destination) {
      console.warn('[traduzione] voce tradotta scartata: audio', isLocal ? 'locale' : 'remoto', 'non collegato alla chiamata');
      channel.queue = [];
      return;
    }
    channel.playing = true;
    if (context.state !== 'running') {
      console.warn('[traduzione] AudioContext', context.state, '-> lo riattivo');
      void context.resume();
    }
    const bytes = Uint8Array.from(atob(channel.queue.shift()!), char => char.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
    const audio = new Audio(url);
    // When phrases pile up the voice speeds up a little (pitch is preserved) instead of falling behind
    audio.playbackRate = channel.queue.length >= 2 ? 1.2 : channel.queue.length === 1 ? 1.1 : 1;
    const source = context.createMediaElementSource(audio);
    source.connect(destination);
    this.duck(isLocal, true);
    let finished = false;
    // A voice that never starts must not block the ones after it
    const startTimer = setTimeout(() => {
      console.warn('[traduzione] voce tradotta non partita, la salto. AudioContext:', context.state);
      done();
    }, PLAY_START_TIMEOUT_MS);
    audio.onplaying = () => {
      clearTimeout(startTimer);
      console.info('[traduzione] voce tradotta', isLocal ? 'inviata al tecnico' : 'riprodotta', `(${audio.duration.toFixed(1)} s)`);
    };
    const done = () => {
      if (finished) {
        return;
      }
      finished = true;
      clearTimeout(startTimer);
      source.disconnect();
      URL.revokeObjectURL(url);
      channel.playing = false;
      if (!channel.queue.length) {
        this.duck(isLocal, false);
      }
      this.playNext(isLocal);
    };
    audio.onended = done;
    audio.onerror = done;
    audio.play().catch(error => {
      console.error('Impossibile riprodurre la traduzione', error);
      done();
    });
  }

  /** Diagnostics: every 5 s logs the loudest level sent to the remote side (0 = the call carries silence). */
  private watchOutgoingLevel(context: AudioContext, destination: MediaStreamAudioDestinationNode): void {
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    context.createMediaStreamSource(destination.stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    let peak = 0;
    const sampler = setInterval(() => {
      analyser.getFloatTimeDomainData(samples);
      peak = Math.max(peak, Math.sqrt(samples.reduce((sum, value) => sum + value * value, 0) / samples.length));
    }, 100);
    const reporter = setInterval(() => {
      if (destination.stream.getAudioTracks().every(track => track.readyState === 'ended') || context.state === 'closed') {
        clearInterval(sampler);
        clearInterval(reporter);
        return;
      }
      console.info(`[traduzione] audio verso il tecnico: livello max ${peak.toFixed(3)}, AudioContext ${context.state}`);
      peak = 0;
    }, 5_000);
  }

  /** While a direction is translated its original voice is muted; otherwise it is only lowered under a translation still playing. */
  private duck(isLocal: boolean, ducked: boolean): void {
    const gain = isLocal ? this.localGain : this.remoteGain;
    const context = isLocal ? this.audioContext : this.remoteAudioContext;
    const muted = this.translating[isLocal ? 'local' : 'remote'];
    if (gain && context) {
      gain.gain.setTargetAtTime(muted ? 0 : ducked ? DUCKED_GAIN : 1, context.currentTime, 0.1);
    }
  }

  private stopRecorder(recorder?: ChunkRecorder): void {
    recorder?.stop();
  }

  private stopMediaRecorder(recorder?: MediaRecorder): void {
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  }

  private toLocale(value: string): string {
    return value.replace(/-([A-Za-z]+)Neural$/, '');
  }

  private toBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve((reader.result as string).split(',', 2)[1]);
      reader.readAsDataURL(blob);
    });
  }

  private sendInfoToServer(info: Record<string, unknown>): void {
    if (!this.backendUrl || !this.speechLogEnabled) {
      return;
    }
    this.http.post(`${this.backendUrl}/backoffice/speech-log`, {
      ...info, tenantId: this.tenantId, connectionId: this.connectionId,
    }).subscribe({ error: () => undefined });
  }
}
